import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth } from '../context/AuthContext'
import {
	getRefusalNotices,
	markRefusalNoticesSeen,
	REFUSAL_CHECK_EVENT,
	type RefusalNotice,
} from '../api/refusals'
import RefusalNoticeModal from './RefusalNoticeModal'

const POLL_MS = 30_000

/** Окна «Отказов нейросетей» в любом разделе сайта: генератор, стикеры, «Сцена», музыка, «Карточка товара»,
 *  мастерская, тренды. Сервер хранит предупреждение до показа (п. 10.3.1 оферты: оно должно дойти до человека),
 *  здесь — забрать и показать по одному, отметить показанное. Отказы подряд сервер сворачивает в одно окно на
 *  группу и решение; новая проверка обновляет такое окно, а не ставит второе. Проверка — при входе, по возвращении
 *  на вкладку, раз в 30 секунд на видимой вкладке и сразу по сигналу провала генерации (requestRefusalCheck). */
export default function RefusalNoticeHost() {
	const { user } = useAuth()
	const userId = user?.id ?? null
	const [queue, setQueue] = useState<RefusalNotice[]>([])
	const busy = useRef(false)
	// Закрытые окна: проверка, успевшая до отметки «показано» на сервере, не вернёт то же окно второй раз.
	const closed = useRef(new Set<string>())

	const check = useCallback(async () => {
		if (!userId || busy.current) return
		busy.current = true
		try {
			const notices = (await getRefusalNotices()).notices.filter((f) => noticeIds(f).some((id) => !closed.current.has(id)))
			if (notices.length) {
				setQueue((q) => {
					const next = q.map((n) => notices.find((f) => sameNotice(f, n)) ?? n)
					return [...next, ...notices.filter((f) => !q.some((n) => sameNotice(f, n)))]
				})
			}
		} catch {
			// сеть или сервер — окно покажем при следующей проверке
		} finally {
			busy.current = false
		}
	}, [userId])

	useEffect(() => {
		if (!userId) {
			setQueue([])
			return
		}
		void check()
		const tick = window.setInterval(() => {
			if (document.visibilityState === 'visible') void check()
		}, POLL_MS)
		const onFocus = () => void check()
		const onSignal = () => void check()
		window.addEventListener('focus', onFocus)
		window.addEventListener(REFUSAL_CHECK_EVENT, onSignal)
		return () => {
			window.clearInterval(tick)
			window.removeEventListener('focus', onFocus)
			window.removeEventListener(REFUSAL_CHECK_EVENT, onSignal)
		}
	}, [userId, check])

	const current = queue[0] ?? null
	const close = useCallback(() => {
		const ids = current ? noticeIds(current) : []
		ids.forEach((id) => closed.current.add(id))
		if (ids.length) void markRefusalNoticesSeen(ids).catch(() => undefined)
		setQueue((q) => q.slice(1))
	}, [current])

	return <RefusalNoticeModal notice={current} onClose={close} />
}

function sameNotice(a: RefusalNotice, b: RefusalNotice) {
	return a.group === b.group && a.action === b.action
}

function noticeIds(n: RefusalNotice) {
	return n.ids ?? (n.id ? [n.id] : [])
}
